import * as React from "react";
import Alert from "./alert";
import { Button, Box, Stack, Divider } from "@mui/material";
import { CollapsableCard } from "../general/collapsable_card";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useMuteAlertDialog } from "../dialogs/mute_alert_dialog";
import { useMuteOverviewDialog } from "../dialogs/mute_overview_dialog";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import useBackend from "../../hooks/use_backend";
import { fetchSiteAlerts, fetchActiveSiteAlertMutes, disableSiteAlertMute } from "../../scripts/api_v2/alerting_client";
import Snackbar from "../../scripts/snackbar";
import { useAmplitudeTrack } from "../../scripts/amplitude_utils";

export default function AlertCard({
  startCollapsed = false,
  tooltip,
  alerts: alertsProp,
  isLoading: isLoadingProp,
  ...props
}) {
  const [count, setCount] = React.useState(9);
  const [showMuted, setShowMuted] = React.useState(true);
  const openMuteDialog = useMuteAlertDialog();
  const openMuteOverview = useMuteOverviewDialog();
  const params = useParams();
  const navigate = useNavigate();
  const { trackButtonClicked } = useAmplitudeTrack();
  const backend = useBackend();
  const queryClient = useQueryClient();

  const shouldFetch = alertsProp === undefined;

  const { data: fetchedAlerts = [], isLoading: fetchedLoading = false } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_ALERTS, backend.fetch, params.site_id],
    queryFn: () => fetchSiteAlerts(backend.fetch, params.site_id),
    enabled: shouldFetch && !!params.site_id,
    refetchInterval: 30000,
    staleTime: 0,
  });

  const { data: mutedAlerts = [] } = useQuery({
    queryKey: [REACT_QUERY_KEYS.ALERT_MUTES, backend.fetch, params.site_id],
    queryFn: () => fetchActiveSiteAlertMutes(backend.fetch, params.site_id),
    enabled: shouldFetch && !!params.site_id,
    refetchInterval: 30000,
    staleTime: 0,
  });

  const alerts = alertsProp !== undefined ? alertsProp : fetchedAlerts;
  const isLoading = isLoadingProp !== undefined ? isLoadingProp : fetchedLoading;

  const unmuteMutation = useMutation({
    mutationFn: (muteId) => disableSiteAlertMute(backend.fetch, muteId),
    onSuccess: async () => {
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.SITE_ALERTS, backend.fetch, params.site_id]);
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.ALERT_MUTES, backend.fetch, params.site_id]);
      Snackbar.success("Alert unmuted successfully");
    },
    onError: (error) => {
      Snackbar.error("Failed to unmute alert: " + error.message);
    },
  });

  const handleMute = async () => {
    await queryClient.invalidateQueries([REACT_QUERY_KEYS.SITE_ALERTS, backend.fetch, params.site_id]);
    await queryClient.invalidateQueries([REACT_QUERY_KEYS.ALERT_MUTES, backend.fetch, params.site_id]);
  };

  const handleUnmute = (mutedAlert) => {
    return unmuteMutation.mutateAsync(mutedAlert.id);
  };

  const handleDeviceClick = (deviceId) => {
    trackButtonClicked('alert_open_device', 'site_alerts_card', { site_id: params.site_id });
    navigate(`/sites/${params.site_id}/devices`, { state: { openDeviceId: deviceId } });
  };

  React.useEffect(() => {
    if (alerts) {
      setCount(Math.min(9, alerts.length));
    }
  }, [isLoading, alerts]);

  const canMute = alertsProp === undefined;

  return (
    <CollapsableCard
      tooltip={tooltip}
      startCollapsed={startCollapsed || (alerts.length === 0 && mutedAlerts.length === 0)}
      title={`Alerts (${alerts.length})`}
      width={"100%"}
      elevation={0}
      sx={props.sx}
    >
      {alerts.slice(0, count).map((alert, idx) => (
        <Alert
          isLoading={isLoading}
          alert={alert}
          key={idx}
          onMute={canMute ? (alert) => openMuteDialog(alert, mutedAlerts, handleMute, handleUnmute) : undefined}
          onDeviceClick={canMute ? handleDeviceClick : undefined}
          isMuted={false}
        />
      ))}

      {mutedAlerts.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {canMute && mutedAlerts.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Button size="small" onClick={() => setShowMuted(!showMuted)} startIcon={<NotificationsOffIcon />}>
                  Muted Alerts ({mutedAlerts.length})
                </Button>
              </Stack>

              {showMuted && (
                <Box sx={{ mt: 1 }}>
                  {mutedAlerts.map((mute, idx) => {
                    const pseudoAlert = {
                      alert_code: mute.alert_code,
                      device_id: mute.device_id,
                      info: mute.info,
                      additional_info: "All alerts of this type",
                      valid_from: mute.valid_from,
                      alert_level: 10,
                      icon: 1,
                    };
                    return (
                      <Alert
                        key={`muted-${idx}`}
                        alert={pseudoAlert}
                        onMute={() => openMuteOverview(mutedAlerts, handleUnmute)}
                        isMuted={true}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </CollapsableCard>
  );
}
